import { Routes, Route, Navigate } from 'react-router-dom'
import {
    DASHBOARD_APP_PREFIX,
    DASHBOARD_APP_SEGMENTS,
    DASHBOARD_AUTH_PATHS as AUTH,
    DASHBOARD_CLAIM_PREFIX,
    DASHBOARD_CONNECT_PATH,
    DASHBOARD_HOME,
    DASHBOARD_PORTAL_PREFIX,
} from '@yourownmetric/shared'

import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { LoginPage } from '@/modules/auth/LoginPage'
import { RegisterPage } from '@/modules/auth/RegisterPage'
import { ForgotPasswordPage } from '@/modules/auth/ForgotPasswordPage'
import { VerifyEmailPage } from '@/modules/auth/VerifyEmailPage'
import { ResetPasswordPage } from '@/modules/auth/ResetPasswordPage'
import { AcceptInvitePage } from '@/modules/auth/AcceptInvitePage'
import { ClaimPage } from '@/modules/claim/ClaimPage'
import { ConnectPage } from '@/modules/connect/ConnectPage'
import { OverviewPage } from '@/modules/analytics/OverviewPage'
import { RealtimePage } from '@/modules/analytics/RealtimePage'
import { SourcesPage } from '@/modules/analytics/SourcesPage'
import { GeoPage } from '@/modules/analytics/GeoPage'
import { DevicesPage } from '@/modules/analytics/DevicesPage'
import { EventsPage } from '@/modules/analytics/EventsPage'
import { EcommercePage } from '@/modules/reports/EcommercePage'
import { GoalsPage } from '@/modules/reports/GoalsPage'
import { FunnelsPage } from '@/modules/reports/FunnelsPage'
import { AttributionPage } from '@/modules/reports/AttributionPage'
import { RetentionPage } from '@/modules/reports/RetentionPage'
import { QualityPage } from '@/modules/reports/QualityPage'
import { SubTenantsPage } from '@/modules/reports/SubTenantsPage'
import { DestinationsPage } from '@/modules/reports/DestinationsPage'
import { UsagePage } from '@/modules/reports/UsagePage'
import { ProjectsPage } from '@/modules/projects/ProjectsPage'
import { SettingsPage } from '@/modules/settings/SettingsPage'
import { ApiKeysPage } from '@/modules/settings/ApiKeysPage'
import { TeamPage } from '@/modules/team/TeamPage'
import { AuditLogPage } from '@/modules/team/AuditLogPage'
import { AccessPage } from '@/modules/team/AccessPage'
import { SecurityPage } from '@/modules/team/SecurityPage'
import { CollectionDomainPage } from '@/modules/settings/CollectionDomainPage'
import { BillingPage } from '@/modules/billing/BillingPage'
import { DashboardsPage } from '@/modules/dashboards/DashboardsPage'
import { AgentsPage } from '@/modules/agents/AgentsPage'
import { LensesPage } from '@/modules/lenses/LensesPage'
import { DocsPage } from '@/modules/docs/DocsPage'
import { AdminPage } from '@/modules/admin/AdminPage'
import { RequireAuth } from '@/components/auth/RequireAuth'
import { RequireSuperAdmin } from '@/components/auth/RequireSuperAdmin'
import { LegacyAppRedirect, RegisterRedirect, RootRedirect } from '@/components/auth/AppRedirects'
import { ProjectGate } from '@/components/shared/ProjectGate'
import { PortalsPage } from '@/modules/portals/PortalsPage'
import { ProofPage } from '@/modules/proof/ProofPage'
import { PrivacyRequestsPage } from '@/modules/privacy/PrivacyRequestsPage'
import { IntentPage } from '@/modules/intent/IntentPage'
import { ExportsPage } from '@/modules/exports/ExportsPage'
import { PortalByHost, PortalBySlug } from '@/modules/portal/PortalApp'
import { appModeForHost, parseAppHosts } from '@/modules/portal/portalHost'

/** Decided once per page load: an agency's custom domain serves only its portal. */
const APP_MODE = appModeForHost(
    globalThis.location?.hostname ?? 'localhost',
    parseAppHosts(import.meta.env.VITE_APP_HOSTS),
)

export function App() {
    if (APP_MODE.kind === 'portal-host') {
        return <PortalByHost host={APP_MODE.host} />
    }
    return (
        <Routes>
            {/* White-label client portals (own session, no dashboard chrome) */}
            <Route path={`${DASHBOARD_PORTAL_PREFIX}:slug/*`} element={<PortalBySlug />} />

            {/* Public auth routes, at the host root (emails and OAuth link here) */}
            <Route element={<AuthLayout />}>
                <Route path={AUTH.login} element={<LoginPage />} />
                <Route path={AUTH.signup} element={<RegisterPage />} />
                <Route path={AUTH.forgotPassword} element={<ForgotPasswordPage />} />
                <Route path={AUTH.verifyEmail} element={<VerifyEmailPage />} />
                <Route path={AUTH.resetPassword} element={<ResetPasswordPage />} />
                <Route path={AUTH.acceptInvite} element={<AcceptInvitePage />} />
                {/* Partner-managed accounts: own the org (claim link, usually in a popup) */}
                <Route path={`${DASHBOARD_CLAIM_PREFIX}:token`} element={<ClaimPage />} />
                {/* YOM Connect: marketplace apps send merchants here (signs in itself, not RequireAuth) */}
                <Route path={DASHBOARD_CONNECT_PATH} element={<ConnectPage />} />
            </Route>
            <Route path={AUTH.register} element={<RegisterRedirect />} />

            {/* Bookmarks from before the app moved under /app */}
            {DASHBOARD_APP_SEGMENTS.map(seg => (
                <Route key={seg} path={`/${seg}/*`} element={<LegacyAppRedirect />} />
            ))}

            {/* Protected dashboard routes */}
            <Route path={DASHBOARD_APP_PREFIX} element={<RequireAuth />}>
                <Route element={<DashboardLayout />}>
                    <Route index element={<Navigate to={DASHBOARD_HOME} replace />} />
                    {/* v2 reports + project-scoped management: need a project of this account */}
                    <Route element={<ProjectGate />}>
                        <Route path="overview" element={<OverviewPage />} />
                        <Route path="realtime" element={<RealtimePage />} />
                        <Route path="sources" element={<SourcesPage />} />
                        <Route path="geo" element={<GeoPage />} />
                        <Route path="devices" element={<DevicesPage />} />
                        <Route path="events" element={<EventsPage />} />
                        <Route path="conversions" element={<EcommercePage />} />
                        <Route path="goals" element={<GoalsPage />} />
                        <Route path="funnels" element={<FunnelsPage />} />
                        <Route path="attribution" element={<AttributionPage />} />
                        <Route path="intent" element={<IntentPage />} />
                        <Route path="retention" element={<RetentionPage />} />
                        <Route path="quality" element={<QualityPage />} />
                        <Route path="sub-tenants" element={<SubTenantsPage />} />
                        <Route path="portals" element={<PortalsPage />} />
                        <Route path="proof" element={<ProofPage />} />
                        <Route path="privacy-requests" element={<PrivacyRequestsPage />} />
                        <Route path="destinations" element={<DestinationsPage />} />
                        <Route path="exports" element={<ExportsPage />} />
                        <Route path="usage" element={<UsagePage />} />
                    </Route>
                    <Route path="projects" element={<ProjectsPage />} />
                    <Route path="dashboards" element={<DashboardsPage />} />
                    <Route path="lenses" element={<LensesPage />} />
                    <Route path="agents" element={<AgentsPage />} />
                    <Route path="docs" element={<DocsPage />} />
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="settings/api-keys" element={<ApiKeysPage />} />
                    <Route path="settings/team" element={<TeamPage />} />
                    <Route path="settings/audit-log" element={<AuditLogPage />} />
                    <Route path="settings/security" element={<SecurityPage />} />
                    <Route path="settings/access" element={<AccessPage />} />
                    <Route path="settings/collection-domain" element={<CollectionDomainPage />} />
                    <Route path="billing" element={<BillingPage />} />
                </Route>

                {/* Superadmin routes */}
                <Route element={<RequireSuperAdmin />}>
                    <Route element={<DashboardLayout />}>
                        <Route path="admin" element={<AdminPage />} />
                    </Route>
                </Route>

                <Route path="*" element={<Navigate to={DASHBOARD_HOME} replace />} />
            </Route>

            {/* `/` and anything else: app when signed in, else login (prod routes `/` to the website) */}
            <Route path="*" element={<RootRedirect />} />
        </Routes>
    )
}
